/**
 * LocalStorageManager
 * Manages all localStorage operations for the Image Generator
 * Handles saving/restoring user state when redirecting to login
 */
class LocalStorageManager {
  constructor(imageGenerator) {
    this.imageGenerator = imageGenerator;
    this.restorationPending = false;
    this.restorationAttempts = 0;
    this.maxRestorationAttempts = 5;
    this.prefix = 'letz_ai_';
    this.keys = {
      selectedImage: 'selected_image',
      galleryData: 'gallery_data',
      userInteractions: 'user_interactions',
      productFilterOnly: 'product_filter_only'
    };
  }

  /**
   * Save data to localStorage with prefix
   */
  save(key, data) {
    try {
      const prefixedKey = this.prefix + key;
      if (typeof data === 'object') {
        localStorage.setItem(prefixedKey, JSON.stringify(data));
      } else {
        localStorage.setItem(prefixedKey, data);
      }
      console.log(`Saved to localStorage: ${prefixedKey}`, data);
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  /**
   * Load data from localStorage with prefix
   */
  load(key, defaultValue = null) {
    try {
      const prefixedKey = this.prefix + key;
      const item = localStorage.getItem(prefixedKey);
      
      if (item === null) {
        return defaultValue;
      }
      
      // Try to parse as JSON, if it fails, return as string
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Remove data from localStorage
   */
  remove(key) {
    try {
      const prefixedKey = this.prefix + key;
      localStorage.removeItem(prefixedKey);
      console.log(`Removed from localStorage: ${prefixedKey}`);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  /**
   * Clear all app-related localStorage data
   */
  clearAll() {
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
      console.log('Cleared all localStorage data');
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Save selected image data
   */
  saveSelectedImage(imageData) {
    this.save(this.keys.selectedImage, imageData);
  }

  /**
   * Load selected image data
   */
  loadSelectedImage() {
    return this.load(this.keys.selectedImage);
  }

  /**
   * Save gallery data
   */
  saveGalleryData(galleryData) {
    this.save(this.keys.galleryData, galleryData);
  }

  /**
   * Load gallery data
   */
  loadGalleryData() {
    return this.load(this.keys.galleryData, []);
  }

  /**
   * Save user interaction data
   */
  saveUserInteraction(interactionType, data) {
    const interactions = this.load(this.keys.userInteractions, {});
    interactions[interactionType] = {
      ...data,
      timestamp: Date.now()
    };
    this.save(this.keys.userInteractions, interactions);
  }

  /**
   * Load user interaction data
   */
  loadUserInteractions() {
    return this.load(this.keys.userInteractions, {});
  }

  /**
   * Check if localStorage is available
   */
  isAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get storage info for debugging
   */
  getStorageInfo() {
    if (!this.isAvailable()) {
      return { available: false };
    }

    const info = {
      available: true,
      items: {},
      totalSize: 0
    };

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.prefix)) {
        const value = localStorage.getItem(key);
        info.items[key] = {
          size: value ? value.length : 0,
          preview: value ? value.substring(0, 100) : ''
        };
        info.totalSize += value ? value.length : 0;
      }
    });

    return info;
  }

  /**
   * Save complete user state before redirecting to login
   * @param {string} inputText - Current input field text
   * @param {Object} selectedCard - Currently selected color card data
   * @param {Object} uploadedImageData - Uploaded image data (for image_card_with_upload)
   */
  saveStateForLogin(inputText, selectedCard, uploadedImageData = null) {
    try {
      // Save current URL for return after login
      const currentUrl = window.location.href;
      localStorage.setItem('letz_ai_return_url', currentUrl);
      
      // Save current input text if it exists
      if (inputText) {
        localStorage.setItem('letz_ai_input_text', inputText);
        console.log('[LocalStorageManager] Input text saved for login return:', inputText);
      }
      
      // Save selected card data if it exists (for card-based products)
      if (selectedCard) {
        const cardDataToSave = {
          name: selectedCard.name,
          display_name: selectedCard.display_name, // Save display_name too
          colors: selectedCard.colors,
          productType: this.imageGenerator.productType
        };
        localStorage.setItem('letz_ai_selected_card', JSON.stringify(cardDataToSave));
        console.log('[LocalStorageManager] Selected card saved for login return:', cardDataToSave);
      }
      
      // Enhanced uploaded image data saving for image_card_with_upload
      if (uploadedImageData && this.imageGenerator.productType === 'image_card_with_upload') {
        // Ensure we have the base64 data from UIHandlers if not provided directly
        if (!uploadedImageData.base64 && this.imageGenerator.uiHandlers) {
          uploadedImageData.base64 = this.imageGenerator.uiHandlers.getUploadedFileBase64();
          console.log('[LocalStorageManager] Retrieved base64 data from UIHandlers');
        }
        
        // Add filename if available from UIHandlers
        if (!uploadedImageData.fileName && this.imageGenerator.uiHandlers && this.imageGenerator.uiHandlers.uploadedFile) {
          uploadedImageData.fileName = this.imageGenerator.uiHandlers.uploadedFile.name;
          console.log('[LocalStorageManager] Retrieved filename from UIHandlers:', uploadedImageData.fileName);
        }
        
        localStorage.setItem('letz_ai_uploaded_image_data', JSON.stringify(uploadedImageData));
        console.log('[LocalStorageManager] Enhanced uploaded image data saved for login return');
      }
      
      // Save product filter preference (Somente deste produto toggle)
      const currentFilterPreference = this.getProductFilterPreference();
      localStorage.setItem('letz_ai_product_filter_preference', JSON.stringify(currentFilterPreference));
      console.log('[LocalStorageManager] Product filter preference saved for login return:', currentFilterPreference);
      
      console.log('[LocalStorageManager] Complete state saved for login return');
    } catch (error) {
      console.error('[LocalStorageManager] Error saving state for login:', error);
    }
  }

  /**
   * Save return URL for login redirect
   * @param {string} url - URL to return to after login
   */
  saveReturnUrl(url) {
    try {
      localStorage.setItem('letz_ai_return_url', url);
    } catch (error) {
      console.error('[LocalStorageManager] Error saving return URL:', error);
    }
  }

  /**
   * Check if user returned from login and show welcome message
   */
  checkForLoginReturn() {
    const returnUrl = localStorage.getItem('letz_ai_return_url');
    
    if (returnUrl && this.imageGenerator.isUserLoggedIn()) {
      console.log('[LocalStorageManager] User returned from login successfully');
      
      // Clear saved return URL from localStorage
      this.clearReturnUrl();
      
      // Show welcome message after successful login
      setTimeout(() => {
        this.imageGenerator.uiHandlers.showToast(window.t('toasts.login_success'), 'success');
      }, 500);
    }
  }

  /**
   * Schedule input restoration with retry mechanism
   */
  scheduleInputRestoration() {
    const savedInputText = localStorage.getItem('letz_ai_input_text');
    const savedSelectedCard = localStorage.getItem('letz_ai_selected_card');
    const savedUploadedImage = localStorage.getItem('letz_ai_uploaded_image_data');
    
    if (!savedInputText && !savedSelectedCard && !savedUploadedImage) {
      console.log('[LocalStorageManager] No saved data to restore');
      return;
    }
    
    if (!this.imageGenerator.isUserLoggedIn()) {
      console.log('[LocalStorageManager] User not logged in, skipping restoration');
      return;
    }
    
    console.log('[LocalStorageManager] Scheduling input restoration with retry mechanism');
    this.restorationPending = true;
    this.attemptInputRestoration();
  }

  /**
   * Attempt input restoration with retry logic
   */
  attemptInputRestoration() {
    if (!this.restorationPending) {
      console.log('[LocalStorageManager] Restoration no longer pending, aborting');
      return;
    }
    
    this.restorationAttempts++;
    console.log(`[LocalStorageManager] Input restoration attempt ${this.restorationAttempts}/${this.maxRestorationAttempts}`);
    
    const savedInputText = localStorage.getItem('letz_ai_input_text');
    const savedSelectedCard = localStorage.getItem('letz_ai_selected_card');
    const savedUploadedImage = localStorage.getItem('letz_ai_uploaded_image_data');
    
    // Parse saved card data
    let savedCardData = null;
    if (savedSelectedCard) {
      try {
        savedCardData = JSON.parse(savedSelectedCard);
        console.log('[LocalStorageManager] Parsed saved card data:', savedCardData);
      } catch (error) {
        console.warn('[LocalStorageManager] Failed to parse saved card data:', error);
      }
    }

    // Parse saved uploaded image data
    let savedUploadedImageData = null;
    if (savedUploadedImage) {
      try {
        savedUploadedImageData = JSON.parse(savedUploadedImage);
        console.log('[LocalStorageManager] Parsed saved uploaded image data');
      } catch (error) {
        console.warn('[LocalStorageManager] Failed to parse saved uploaded image data:', error);
      }
    }
    
    // Check if we have the required data for card-based products
    const needsCardData = this.imageGenerator.productType === 'color_card' || 
                         this.imageGenerator.productType === 'image_card' || 
                         this.imageGenerator.productType === 'image_card_with_upload';
    const hasCardData = this.imageGenerator.colorCardData && 
                       Array.isArray(this.imageGenerator.colorCardData) && 
                       this.imageGenerator.colorCardData.length > 0;
    
    console.log('[LocalStorageManager] Restoration check:', {
      needsCardData,
      hasCardData,
      colorCardDataLength: this.imageGenerator.colorCardData?.length || 0,
      productType: this.imageGenerator.productType,
      savedInputText: !!savedInputText,
      savedCardData: !!savedCardData,
      savedUploadedImageData: !!savedUploadedImageData
    });
    
    if (needsCardData && !hasCardData) {
      if (this.restorationAttempts < this.maxRestorationAttempts) {
        console.log('[LocalStorageManager] Color card data not ready, retrying in 1 second...');
        setTimeout(() => this.attemptInputRestoration(), 1000);
        return;
      } else {
        console.warn('[LocalStorageManager] Max restoration attempts reached, proceeding without card data');
      }
    }
    
    // Proceed with restoration
    this.restorationPending = false;
    this.performInputRestoration(savedInputText, savedCardData, savedUploadedImageData);
  }

  /**
   * Perform the actual input restoration
   * @param {string} savedInputText - Saved input text
   * @param {Object} savedCardData - Saved color card data
   * @param {Object} savedUploadedImageData - Saved uploaded image data
   */
  performInputRestoration(savedInputText, savedCardData, savedUploadedImageData) {
    console.log('[LocalStorageManager] Performing input restoration:', { 
      savedInputText, 
      savedCardData, 
      hasUploadedImage: !!savedUploadedImageData 
    });
    
    // Restore uploaded image data first (for image_card_with_upload)
    if (savedUploadedImageData && this.imageGenerator.productType === 'image_card_with_upload') {
      this.restoreUploadedImageData(savedUploadedImageData);
    }
    
    // Restore input text
    if (savedInputText && this.imageGenerator.input) {
      console.log('[LocalStorageManager] Restoring input text:', savedInputText);
      this.imageGenerator.input.value = savedInputText;
      
      // For color_card products, show the input but keep it locked
      if (this.imageGenerator.productType === 'color_card') {
        const inputContainer = this.imageGenerator.colorCardManager.getInputFieldContainer();
        if (inputContainer) {
          inputContainer.classList.remove('letz-ai__input-hidden');
          console.log('[LocalStorageManager] Input field shown for color_card after text restoration');
        }
        
        // Keep the input disabled for color_card products - use lock method
        if (this.imageGenerator.colorCardManager) {
          this.imageGenerator.colorCardManager.lockInputWhenFilled();
        }
        
        // Mark as shown once in ColorCardManager
        if (this.imageGenerator.colorCardManager) {
          this.imageGenerator.colorCardManager.inputShownOnce = true;
        }
        
        console.log('[LocalStorageManager] Input kept locked for color_card after text restoration');
      } else {
        // For non-color_card products, enable the input for editing
        this.imageGenerator.input.disabled = false;
        this.imageGenerator.input.style.cursor = 'text';
        this.imageGenerator.input.style.pointerEvents = 'auto';
        
        // Focus on input and position cursor at end for non-color_card products
        this.imageGenerator.input.focus();
        this.imageGenerator.input.setSelectionRange(savedInputText.length, savedInputText.length);
      }
      
      // Dispatch input event to trigger any handlers
      this.imageGenerator.input.dispatchEvent(new Event('input', { bubbles: true }));
      
      // Update field content class
      if (this.imageGenerator.colorCardManager) {
        this.imageGenerator.colorCardManager.updateFieldContentClass();
      }
    }
    
    // Try to find and select matching color card for ALL card-based products
    if ((this.imageGenerator.productType === 'color_card' || 
         this.imageGenerator.productType === 'image_card' || 
         this.imageGenerator.productType === 'image_card_with_upload') && 
         this.imageGenerator.colorCardData) {
      this.restoreColorCardSelection(savedCardData, savedInputText);
    }
    
    // Restore product filter preference (Somente deste produto toggle)
    this.restoreProductFilterPreference();
    
    // Update button status
    this.imageGenerator.uiHandlers.updateButtonStatus();
    
    // Clear from localStorage
    this.clearRestorationData();
    
    console.log('[LocalStorageManager] Input text and card selection restored, localStorage cleared');
  }

  /**
   * Restore color card selection
   * @param {Object} savedCardData - Saved color card data
   * @param {string} savedInputText - Saved input text as fallback
   */
  restoreColorCardSelection(savedCardData, savedInputText) {
    let matchingCard = null;
    
    // First try to match using saved card data (more reliable)
    if (savedCardData) {
      // Try display_name first (most reliable for current interface)
      if (savedCardData.display_name) {
        matchingCard = this.imageGenerator.colorCardData.find(card => 
          card.display_name === savedCardData.display_name
        );
        console.log('[LocalStorageManager] Looking for saved card by display_name:', savedCardData.display_name, 'Found:', !!matchingCard);
      }
      
      // Fallback to name if display_name didn't match
      if (!matchingCard && savedCardData.name) {
        matchingCard = this.imageGenerator.colorCardData.find(card => 
          card.name === savedCardData.name
        );
        console.log('[LocalStorageManager] Fallback: Looking for saved card by name:', savedCardData.name, 'Found:', !!matchingCard);
      }
    }
    
    // Final fallback: match by input text
    if (!matchingCard && savedInputText) {
      matchingCard = this.imageGenerator.colorCardData.find(card => 
        card.name.toLowerCase() === savedInputText.toLowerCase().trim()
      );
      console.log('[LocalStorageManager] Final fallback: Looking for card by input text:', savedInputText, 'Found:', !!matchingCard);
    }
    
    if (matchingCard && this.imageGenerator.colorCardsContainer) {
      // Determine the correct CSS selector based on product type
      let cardSelector;
      let cardNameSelector;
      
      if (this.imageGenerator.productType === 'color_card') {
        cardSelector = '.letz-ai__color-card';
        cardNameSelector = '.letz-ai__color-card-name';
      } else if (this.imageGenerator.productType === 'image_card' || this.imageGenerator.productType === 'image_card_with_upload') {
        cardSelector = '.letz-ai__image-card';
        cardNameSelector = '.letz-ai__image-card-name';
      }
      
      console.log(`[LocalStorageManager] Using selector "${cardSelector}" for product type "${this.imageGenerator.productType}"`);
      
      // Find and select the matching card element
      const cardElements = this.imageGenerator.colorCardsContainer.querySelectorAll(cardSelector);
      console.log(`[LocalStorageManager] Found ${cardElements.length} card elements with selector "${cardSelector}"`);
      
      cardElements.forEach(cardEl => {
        const cardNameElement = cardEl.querySelector(cardNameSelector);
        const cardName = cardNameElement?.textContent;
        console.log(`[LocalStorageManager] Checking card element with name: "${cardName}"`);
        
        // Compare with display_name first (interface shows display_name), then fallback to name
        const cardMatches = (cardName === matchingCard.display_name) || (cardName === matchingCard.name);
        console.log(`[LocalStorageManager] Comparing card "${cardName}" with saved card display_name:"${matchingCard.display_name}" name:"${matchingCard.name}" - Match: ${cardMatches}`);
        
        if (cardMatches) {
          // Remove selection from other cards
          this.imageGenerator.colorCardsContainer.querySelectorAll(`${cardSelector}.selected`).forEach(selectedCard => {
            selectedCard.classList.remove('selected');
          });
          
          // Select this card
          cardEl.classList.add('selected');
          this.imageGenerator.selectedColorCard = matchingCard;
          if (this.imageGenerator.colorCardManager) {
            this.imageGenerator.colorCardManager.selectedColorCard = matchingCard;
          }
          
          // For both image_card and image_card_with_upload, trigger the card selection logic to show input and preview
          if (this.imageGenerator.productType === 'image_card' || this.imageGenerator.productType === 'image_card_with_upload') {
            // Simulate the card click to trigger all necessary UI updates
            cardEl.click();
          }
          
          console.log(`[LocalStorageManager] Auto-selected matching card for ${this.imageGenerator.productType}:`, matchingCard.name);
        }
      });
    } else {
      console.log(`[LocalStorageManager] No matching card found for ${this.imageGenerator.productType} product type`);
      if (!this.imageGenerator.colorCardData || this.imageGenerator.colorCardData.length === 0) {
        console.warn('[LocalStorageManager] Color card data is empty or not loaded');
      } else {
        console.log('[LocalStorageManager] Available cards:', this.imageGenerator.colorCardData.map(card => card.name));
      }
    }
  }

  /**
   * Enhanced restore uploaded image data for image_card_with_upload products
   * @param {Object} uploadedImageData - Saved uploaded image data
   */
  restoreUploadedImageData(uploadedImageData) {
    try {
      console.log('[LocalStorageManager] Restoring uploaded image data for image_card_with_upload');
      
      if (uploadedImageData.base64 && this.imageGenerator.uiHandlers) {
        // First, update the UIHandlers with the restored data
        this.imageGenerator.uiHandlers.uploadedFileBase64 = uploadedImageData.base64;
        console.log('[LocalStorageManager] Base64 data restored to UIHandlers');
        
        // Create a mock file object if we have filename
        if (uploadedImageData.fileName) {
          // Create a pseudo-file object for consistency
          this.imageGenerator.uiHandlers.uploadedFile = {
            name: uploadedImageData.fileName,
            type: 'image/*', // Generic image type
            size: 0 // Size not critical for display
          };
          console.log('[LocalStorageManager] Mock file object created:', uploadedImageData.fileName);
        }
        
        // Update the integrated preview image
        const previewImg = document.querySelector('[data-integrated-preview-img]');
        if (previewImg) {
          previewImg.src = uploadedImageData.base64;
          console.log('[LocalStorageManager] Preview image src updated');
        }
        
        // Show the preview area and hide drop zone using UIHandlers method
        this.imageGenerator.uiHandlers.showImagePreview();
        console.log('[LocalStorageManager] Image preview shown via UIHandlers');
        
        // Show success message to user
        setTimeout(() => {
          this.imageGenerator.uiHandlers.showToast(window.t('toasts.image_restored_success'), 'success');
        }, 500);
        
        console.log('[LocalStorageManager] Uploaded image data restored successfully');
      } else {
        console.warn('[LocalStorageManager] Missing base64 data or UIHandlers for image restoration');
      }
    } catch (error) {
      console.error('[LocalStorageManager] Error restoring uploaded image data:', error);
      // Show error message to user
      if (this.imageGenerator.uiHandlers) {
        this.imageGenerator.uiHandlers.showToast(window.t('toasts.image_restore_error'), 'error');
      }
    }
  }

  /**
   * Save product filter preference (Somente deste produto toggle)
   * @param {boolean} onlyThisProduct - Whether to show only this product's images
   */
  saveProductFilterPreference(onlyThisProduct) {
    try {
      this.save(this.keys.productFilterOnly, onlyThisProduct);
      localStorage.setItem('letz_ai_product_filter_only', JSON.stringify(onlyThisProduct));
      console.log('[LocalStorageManager] Product filter preference saved:', onlyThisProduct);
    } catch (error) {
      console.error('[LocalStorageManager] Error saving product filter preference:', error);
    }
  }

  /**
   * Get product filter preference (defaults to false)
   * @returns {boolean} Whether to show only this product's images
   */
  getProductFilterPreference() {
    try {
      const preference = this.load(this.keys.productFilterOnly, false);
      console.log('[LocalStorageManager] Product filter preference loaded:', preference);
      return preference;
    } catch (error) {
      console.error('[LocalStorageManager] Error loading product filter preference:', error);
      return false; // Default to false on error
    }
  }

  /**
   * Restore product filter preference after login
   */
  restoreProductFilterPreference() {
    try {
      const savedPreference = localStorage.getItem('letz_ai_product_filter_preference');
      if (savedPreference !== null) {
        const preference = JSON.parse(savedPreference);
        
        // Find the toggle checkbox and update its state
        const toggleCheckbox = document.querySelector('.letz-ai__product-filter-checkbox');
        if (toggleCheckbox) {
          toggleCheckbox.checked = preference;
          
          // Trigger change event to update the gallery
          toggleCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
          
          console.log('[LocalStorageManager] Product filter preference restored:', preference);
        } else {
          console.warn('[LocalStorageManager] Product filter checkbox not found for restoration');
        }
        
        // Also save to permanent storage
        this.saveProductFilterPreference(preference);
        
        // Remove from temporary login storage
        localStorage.removeItem('letz_ai_product_filter_preference');
      }
    } catch (error) {
      console.error('[LocalStorageManager] Error restoring product filter preference:', error);
    }
  }

  /**
   * Clear all restoration data from localStorage
   */
  clearRestorationData() {
    try {
      localStorage.removeItem('letz_ai_input_text');
      localStorage.removeItem('letz_ai_selected_card');
      localStorage.removeItem('letz_ai_uploaded_image_data');
      localStorage.removeItem('letz_ai_product_filter_preference');
      console.log('[LocalStorageManager] Restoration data cleared from localStorage');
    } catch (error) {
      console.error('[LocalStorageManager] Error clearing restoration data:', error);
    }
  }

  /**
   * Clear return URL from localStorage
   */
  clearReturnUrl() {
    try {
      localStorage.removeItem('letz_ai_return_url');
    } catch (error) {
      console.error('[LocalStorageManager] Error clearing return URL:', error);
    }
  }

  /**
   * Check if there is restoration data available
   * @returns {boolean} True if restoration data exists
   */
  hasRestorationData() {
    const hasInput = !!localStorage.getItem('letz_ai_input_text');
    const hasCard = !!localStorage.getItem('letz_ai_selected_card');
    const hasImage = !!localStorage.getItem('letz_ai_uploaded_image_data');
    return hasInput || hasCard || hasImage;
  }

  /**
   * Get all restoration data from localStorage
   * @returns {Object} Object containing all restoration data
   */
  getRestorationData() {
    try {
      const inputText = localStorage.getItem('letz_ai_input_text');
      const selectedCard = localStorage.getItem('letz_ai_selected_card');
      const uploadedImage = localStorage.getItem('letz_ai_uploaded_image_data');
      
      return {
        inputText,
        selectedCard: selectedCard ? JSON.parse(selectedCard) : null,
        uploadedImage: uploadedImage ? JSON.parse(uploadedImage) : null,
        returnUrl: localStorage.getItem('letz_ai_return_url')
      };
    } catch (error) {
      console.error('[LocalStorageManager] Error getting restoration data:', error);
      return {};
    }
  }
}

// Export the class globally
window.LocalStorageManager = LocalStorageManager;

// Auto-initialize and expose globally
window.localStorageManager = new LocalStorageManager();

console.log('LocalStorageManager initialized:', window.localStorageManager.getStorageInfo());
